/** Helpers for showing repo images at full quality in the browser. */

const NATIVE_IMG_TYPES = /^image\/(jpeg|png|gif|webp)$/i;
const HEIC_TYPES = /^image\/(heic|heif)$/i;

/** Guess MIME from magic bytes when GitHub/raw fetch omits a useful type. */
export function sniffImageMime(bytes: Uint8Array): string | null {
  if (bytes.length < 12) return null;
  // JPEG SOI is 0xFFD8 only (next byte is a marker, not always 0xFF).
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return 'image/jpeg';
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'image/png';
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) return 'image/gif';
  if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46) return 'image/webp';
  // HEIC / HEIF (ftyp box)
  if (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
    const brand = String.fromCharCode(bytes[8]!, bytes[9]!, bytes[10]!, bytes[11]!);
    if (brand.startsWith('heic') || brand.startsWith('heix') || brand.startsWith('mif1')) {
      return 'image/heic';
    }
  }
  return null;
}

/** Only HEIC/HEIF need conversion for <img>; JPEG/PNG/WebP are shown as stored. */
export function needsBrowserImageConversion(mime: string): boolean {
  return HEIC_TYPES.test(mime);
}

/** Convert HEIC to high-quality JPEG for display (full resolution, no resize). */
export async function blobForBrowserImageDisplay(blob: Blob): Promise<Blob> {
  if (NATIVE_IMG_TYPES.test(blob.type)) return blob;
  if (!needsBrowserImageConversion(blob.type)) return blob;

  try {
    const bitmap = await createImageBitmap(blob);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return blob;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(bitmap, 0, 0);
      const jpeg = await new Promise<Blob | null>(resolve =>
        canvas.toBlob(resolve, 'image/jpeg', 0.98)
      );
      return jpeg ?? blob;
    } finally {
      bitmap.close();
    }
  } catch {
    return blob;
  }
}
